import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

export default function AdminAdministrators() {
  const [admins, setAdmins] = useState(null);
  const [allPerms, setAllPerms] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPerms, setNewPerms] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/admins").then(({ data }) => { setAdmins(data.admins); setAllPerms(data.all_permissions); }).catch(() => {});
  useEffect(() => { load(); api.get("/admin/me").then(({ data }) => setIsOwner(data.platform_role === "owner")).catch(() => {}); }, []);

  const twofa = () => window.prompt("Enter your current 6-digit 2FA code to confirm:");
  const togglePerm = (list, setList, p) => setList(list.includes(p) ? list.filter((x) => x !== p) : [...list, p]);

  const addAdmin = async () => {
    setBusy(true);
    try { await api.post("/admin/admins", { email: newEmail, permissions: newPerms }); toast.success("Administrator added"); setNewEmail(""); setNewPerms([]); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const savePerms = async (a, perms) => {
    try { await api.patch(`/admin/admins/${a.id}`, { permissions: perms }); toast.success("Permissions updated"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const suspend = async (a) => {
    try { await api.post(`/admin/admins/${a.id}/suspend`, { disabled: !a.disabled }); toast.success(a.disabled ? "Reactivated" : "Suspended"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.email}?`)) return;
    try { await api.delete(`/admin/admins/${a.id}`); toast.success("Admin access removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const reset2fa = async (a) => {
    const code = twofa(); if (!code) return;
    try { await api.post(`/admin/admins/${a.id}/reset-2fa`, {}, { headers: { "X-2FA-Code": code } }); toast.success("2FA reset"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const revoke = async (a) => {
    try { await api.post(`/admin/admins/${a.id}/revoke-sessions`); toast.success("Sessions revoked"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Administrators" subtitle="Owner-only: manage who can access the admin platform and what they can do">
      {!admins ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : !isOwner ? (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 px-4 py-3 text-sm">Only the Owner can manage administrators.</div>
      ) : (
        <div className="space-y-6" data-testid="admin-administrators">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="text-sm font-bold mb-3">Invite an administrator</h3>
            <p className="text-xs text-muted-foreground mb-3">The person must already have a registered account. Granting admin access does not expose or change their password.</p>
            <div className="flex flex-wrap gap-2 items-center">
              <input data-testid="new-admin-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="user@email.com" className="rounded-xl border border-input bg-background px-3 py-2 text-sm w-64 outline-none focus:ring-2 focus:ring-primary/40" />
              <button data-testid="add-admin" onClick={addAdmin} disabled={busy || !newEmail} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60">Add admin</button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {allPerms.map((p) => (
                <button key={p} onClick={() => togglePerm(newPerms, setNewPerms, p)} className={`text-xs px-2 py-1 rounded-full border capitalize ${newPerms.includes(p) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{p}</button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {admins.map((a) => <AdminRow key={a.id} a={a} allPerms={allPerms} onSave={savePerms} onSuspend={suspend} onRemove={remove} onReset2fa={reset2fa} onRevoke={revoke} />)}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function AdminRow({ a, allPerms, onSave, onSuspend, onRemove, onReset2fa, onRevoke }) {
  const [perms, setPerms] = useState(a.permissions || []);
  const isOwnerRow = a.platform_role === "owner";
  const toggle = (p) => setPerms(perms.includes(p) ? perms.filter((x) => x !== p) : [...perms, p]);
  const dirty = JSON.stringify([...perms].sort()) !== JSON.stringify([...(a.permissions || [])].sort());
  return (
    <div data-testid={`admin-row-${a.id}`} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold flex items-center gap-2 flex-wrap">{a.name || a.email}
            <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-primary/10 text-primary">{a.platform_role}</span>
            {a.totp_enabled && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">2FA</span>}
            {a.disabled && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-500">suspended</span>}
            {a.is_test_admin && <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">test</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{a.email} · last login {a.last_login ? new Date(a.last_login).toLocaleString() : "never"}</div>
        </div>
        {!isOwnerRow && (
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => onSuspend(a)} className="text-xs rounded-lg border border-border px-2.5 py-1.5 hover:bg-accent">{a.disabled ? "Reactivate" : "Suspend"}</button>
            <button onClick={() => onReset2fa(a)} className="text-xs rounded-lg border border-border px-2.5 py-1.5 hover:bg-accent">Reset 2FA</button>
            <button onClick={() => onRevoke(a)} className="text-xs rounded-lg border border-border px-2.5 py-1.5 hover:bg-accent">Revoke sessions</button>
            <button onClick={() => onRemove(a)} className="text-xs rounded-lg border border-red-500/40 text-red-500 px-2.5 py-1.5 hover:bg-red-500/10">Remove</button>
          </div>
        )}
      </div>
      {!isOwnerRow && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-1.5">
            {allPerms.map((p) => (
              <button key={p} onClick={() => toggle(p)} className={`text-xs px-2 py-1 rounded-full border capitalize ${perms.includes(p) ? "bg-primary/15 text-primary border-primary/40" : "border-border text-muted-foreground"}`}>{p}</button>
            ))}
          </div>
          {dirty && <button data-testid={`save-perms-${a.id}`} onClick={() => onSave(a, perms)} className="mt-2 text-xs rounded-lg bg-primary text-primary-foreground px-3 py-1.5 font-semibold">Save permissions</button>}
        </div>
      )}
    </div>
  );
}
